import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  capitalize,
  ISettingsGroup,
  PLURAL_KEY,
  RoutesAdmin,
  usePrefixedRouting,
} from "@ryde/shared";

import { useCreateSettingsItem } from "./use-create-settings-item";

interface ISettingsData {
  settingsDataAdmin: ISettingsGroup[];
}

export const useSettingsDataAdmin = (): ISettingsData => {
  const { t } = useTranslation();
  const { addPrefix } = usePrefixedRouting();
  const { createDefaultMenuItemAdmin, createDefaultMenuItemAdminNoPermission } =
    useCreateSettingsItem();
  const settingsDataAdmin = useMemo(() => {
    return [
      {
        groupTitle: capitalize(t(`company.${PLURAL_KEY}`)),
        groupItems: [createDefaultMenuItemAdmin("company")],
      },
      {
        groupTitle: capitalize(t("auditLog.plural")),
        groupItems: [
          createDefaultMenuItemAdmin("auditLog"),
          createDefaultMenuItemAdmin("providerLog"),
          createDefaultMenuItemAdmin("messagingLog"),
        ],
      },
      {
        groupTitle: capitalize(t("aiConversation.plural")),
        groupItems: [
          createDefaultMenuItemAdmin("aiConversation"),
          createDefaultMenuItemAdmin("aiConversation", {
            titleTranslationKey: "aiConversation.stats.title",
            href: addPrefix(RoutesAdmin.AI_CONVERSATION_STATS),
          }),
        ],
      },
      {
        groupTitle: capitalize(t("process.processDrawerTitle")),
        groupItems: [
          createDefaultMenuItemAdmin("process", {
            titleTranslationKey: "process.processDrawerTitle",
          }),
          createDefaultMenuItemAdmin("supplierQuarantine"),
        ],
      },
      {
        groupTitle: capitalize(t("report.REPORT_MANAGEMENT_TITLE")),
        groupItems: [
          createDefaultMenuItemAdmin("report", {
            titleTranslationKey: "report.REPORT_MANAGEMENT_TITLE",
          }),
        ],
      },
      {
        groupTitle: capitalize(t("backup.plural")),
        groupItems: [createDefaultMenuItemAdmin("backup")],
      },
      {
        groupTitle: capitalize(t("apiKey.plural")),
        groupItems: [createDefaultMenuItemAdmin("apiKey")],
      },
      {
        groupTitle: capitalize(t("general.FIELDS")),
        groupItems: [
          createDefaultMenuItemAdmin("fieldType"),
          createDefaultMenuItemAdmin("customField"),
          createDefaultMenuItemAdmin("formSchema"),
          createDefaultMenuItemAdmin("gridSchema"),
        ],
      },
      {
        groupTitle: capitalize(t(`etl.${PLURAL_KEY}`)),
        groupItems: [
          createDefaultMenuItemAdmin("etlJobLog"),
          createDefaultMenuItemAdmin("etlSchedule"),
          createDefaultMenuItemAdminNoPermission("mapCacheStat"),
        ],
      },
      {
        groupTitle: capitalize(t("adminTools.groupTitle")),
        groupItems: [
          createDefaultMenuItemAdminNoPermission("adminTools"),
          createDefaultMenuItemAdmin("adminAction"),
        ],
      },
      {
        groupTitle: capitalize(t(`user.${PLURAL_KEY}`)),
        groupItems: [
          createDefaultMenuItemAdmin("permission"),
          createDefaultMenuItemAdmin("role"),
          createDefaultMenuItemAdmin("user"),
        ],
      },
      {
        groupTitle: capitalize(t(`alertConfiguration.${PLURAL_KEY}`)),
        groupItems: [createDefaultMenuItemAdmin("alertConfiguration")],
      },
      {
        groupTitle: capitalize(t(`webhook.${PLURAL_KEY}`)),
        groupItems: [
          createDefaultMenuItemAdmin("webhook"),
          createDefaultMenuItemAdmin("webhookDelivery"),
        ],
      },
      {
        groupTitle: capitalize(t(`globalConfig.${PLURAL_KEY}`)),
        groupItems: [
          createDefaultMenuItemAdmin("globalConfig"),
          createDefaultMenuItemAdmin("busStation"),
          createDefaultMenuItemAdmin("geoArea"),
        ],
      },
    ];
  }, [
    addPrefix,
    createDefaultMenuItemAdmin,
    createDefaultMenuItemAdminNoPermission,
    t,
  ]);

  return {
    settingsDataAdmin,
  };
};
